import { useNavigate } from "react-router-dom";
import { ClipboardEdit, Clock, CheckCircle2, AlertTriangle, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SELLER_STAGE } from "@/lib/applyApi";

export const AD_LABEL = {
  waiting_on_seller: "Waiting on Seller",
  draft: "Draft",
  submitted: "Submitted — Awaiting Admin Review",
  changes_requested: "Changes Requested",
  approved: "Approved",
};
const AD_TONE = {
  waiting_on_seller: "bg-amber-50 text-amber-900 border-amber-300",
  draft: "bg-stone-100 text-stone-700 border-stone-300",
  submitted: "bg-blue-50 text-blue-700 border-blue-200",
  changes_requested: "bg-orange-50 text-orange-800 border-orange-300",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export function AdStatusPill({ status, testId, prefix = "", label }) {
  if (!status) return null;
  return (
    <span data-testid={testId} className={`inline-flex border rounded-full px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap ${AD_TONE[status] || AD_TONE.draft}`}>
      {label || `${prefix}${AD_LABEL[status] || status}`}
    </span>
  );
}

export function StatusLines({ a, testId }) {
  const contract = { signed: "Signed", awaiting_seller: "Awaiting your signature", amendment_awaiting_seller: "Amendment awaiting signature" }[a.contract_status]
    || (a.status === "approved" ? "Being prepared" : "After approval");
  const rows = [["Application", a.status === "draft" ? "Draft" : SELLER_STAGE[a.status] || a.status], ["Contract", contract],
    ["Ad Template", a.ad_template_status ? AD_LABEL[a.ad_template_status] : "Not started"]];
  return (
    <dl data-testid={testId} className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs mt-1">
      {rows.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-stone-400 font-semibold">{k}:</dt>
          <dd data-testid={`${testId}-${k.toLowerCase().replace(" ", "-")}`} className="text-stone-800 font-semibold">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

export function AdTemplateAdminCard({ a }) {
  const navigate = useNavigate();
  if (!a.horse_id) return null;
  return (
    <section data-testid="admin-ad-template-card" className="bg-white border border-stone-200 rounded-xl p-4">
      <h2 className="font-semibold text-stone-900 mb-2">Horse Ad Template</h2>
      <AdStatusPill status={a.ad_template_status} testId="admin-ad-template-status"
        label={a.ad_template_status === "waiting_on_seller" ? "Waiting on Ad Template" : undefined} />
      <div className="mt-3 grid grid-cols-2 gap-2">
        <Button data-testid="admin-open-ad-template" variant="outline" onClick={() => navigate(`/listing/${a.horse_id}`)} className="h-10 rounded-full text-xs col-span-2">View Ad Template (incl. papers)</Button>
        <Button data-testid="admin-open-ad-builder" variant="outline" onClick={() => navigate(`/horse/${a.horse_id}`)} className="h-10 rounded-full text-xs">Open Ad Builder</Button>
        <Button data-testid="admin-open-review-queue" variant="outline" onClick={() => navigate("/review")} className="h-10 rounded-full text-xs">Review Queue</Button>
      </div>
    </section>
  );
}

const CTA = {
  waiting_on_seller: ["Complete Horse Ad Template", ClipboardEdit, "bg-amber-800 hover:bg-amber-900 text-white"],
  draft: ["Continue Horse Ad Template", ClipboardEdit, "bg-amber-800 hover:bg-amber-900 text-white"],
  changes_requested: ["Update Horse Ad Template", AlertTriangle, "bg-orange-700 hover:bg-orange-800 text-white"],
  submitted: ["Ad Submitted — Awaiting Review", Clock, "bg-blue-50 text-blue-800 border border-blue-200 hover:bg-blue-100"],
  approved: ["Ad Approved — View / Update Ad", CheckCircle2, "bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-100"],
};

const SHORT = { submitted: "Ad Submitted", approved: "View / Update Ad" };

export function AdTemplateButton({ a, testId, compact }) {
  const navigate = useNavigate();
  if (!a.horse_id || !a.ad_template_status) return null;
  const [label, Icon, tone] = CTA[a.ad_template_status] || CTA.draft;
  const viewOnly = ["submitted", "approved"].includes(a.ad_template_status);
  if (compact) {
    // My Horses card: shares a row with Open Horse; the card shows the review note itself.
    return (
      <Button data-testid={testId} onClick={(e) => { e.stopPropagation(); navigate(`/listing/${a.horse_id}`); }}
        className={`flex-1 min-w-0 h-9 px-2.5 text-xs rounded-full font-bold ${tone}`}>
        <Icon className="h-3.5 w-3.5 mr-1 shrink-0" /><span className="truncate">{SHORT[a.ad_template_status] || label.replace("Horse Ad Template", "Ad Template")}</span>
      </Button>
    );
  }
  return (
    <div className="mt-3">
      {a.ad_review_note && (
        <p data-testid={`${testId}-note`} className="mb-2 text-xs text-orange-900 bg-orange-50 border border-orange-200 rounded-lg p-2"><strong>Changes requested:</strong> {a.ad_review_note}</p>
      )}
      <Button data-testid={testId} onClick={(e) => { e.stopPropagation(); navigate(`/listing/${a.horse_id}`); }}
        className={`w-full sm:w-auto h-12 px-6 text-sm rounded-full font-bold ${tone}`}>
        <Icon className="h-4 w-4 mr-2" />{label}
        {viewOnly && <Eye className="h-4 w-4 ml-2 opacity-70" />}
      </Button>
    </div>
  );
}
